/**
 * SQL 품질 검사 — 검사 기준 → 대상 → 검사 실행 → 결과(현황·상세·검토) → 리포트, 그리고 이력(docs/features/27-sql-quality.md).
 * 1차 범위(사용자 결정 2026-10-19): 검사 유형 가(표준 준수)·나(오류 로직 후보)·마(성능 목표) + 대상(공유 풀·AWR·CSV) + 결과·이력.
 *
 * 틀은 SQL 튜닝·진단 리포트·작성 창과 같다(왼쪽 작업 목록 = 검사 이력 · 단계 리본). 기계 검사는 AI 와 무관하게 돌고,
 * AI 판정은 사람이 고른 문장에만(한 번에 10건까지) · 검증 쿼리는 사람이 누를 때만 실행한다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiCheckSquare, FiPlus, FiSliders, FiDownload, FiRefreshCw, FiSquare, FiUpload } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/sqlQuality';
import * as collectApi from '../../services/sqlCollect';
import * as guardrailApi from '../../services/guardrail';
import { guardExport } from '../../utils/guardrailGate';
import ReportView from '../sqlTuning/ReportView';
import CriteriaEditor, { describeChange, CriteriaSummary } from './CriteriaEditor';
import QualityResults from './QualityResults';
import '../sqlReview/SqlReviewModal.css';
import '../sqlAuthor/SqlAuthorModal.css';
import './SqlQualityModal.css';
import memoWindow from '../common/memoWindow';
import WorkflowHead, { WorkflowRibbon } from '../common/WorkflowHead';

const TABS = ['criteria', 'target', 'results', 'status', 'history'];
const KINDS = ['select', 'insert', 'update', 'delete', 'merge'];
// PostgreSQL 은 pg_stat_statements 를 공유 풀로 쓴다(AWR 없음, 2026-10-19 2차)
const SUPPORTED = ['oracle', 'tibero', 'postgresql'];
const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));
const emptyTarget = () => ({ source: 'pool', kinds: ['select'], schema: '', module: '', q: '', limit: 1000, minExec: 1, days: 7, csvText: '', csvName: '', replace: true, awrOk: false, setId: '', text: 'clean' });

function Bars({ title, data, label }) {
  const entries = Object.entries(data || {}).sort((a, b) => (typeof b[1] === 'number' ? b[1] : b[1].flagged) - (typeof a[1] === 'number' ? a[1] : a[1].flagged));
  const max = Math.max(1, ...entries.map(([, v]) => (typeof v === 'number' ? v : v.items)));
  return (
    <div className="stn-box">
      <b>{title}</b>
      {!entries.length && <span className="stn-note">-</span>}
      {entries.map(([k, v]) => {
        const n = typeof v === 'number' ? v : v.flagged;
        const all = typeof v === 'number' ? null : v.items;
        return (
          <div key={k || '-'} className="sqq-bar-row">
            <span className="sqq-bar-label">{label(k)}</span>
            <span className="sqq-bar">
              {all != null && <span className="sqq-bar-all" style={{ width: `${(all / max) * 100}%` }} />}
              <span className="sqq-bar-fill" style={{ width: `${(n / max) * 100}%` }} />
            </span>
            <span className="sqq-bar-n mono">{all != null ? `${fmt(n)} / ${fmt(all)}` : fmt(n)}</span>
          </div>
        );
      })}
    </div>
  );
}
Bars.propTypes = { title: PropTypes.string.isRequired, data: PropTypes.object, label: PropTypes.func.isRequired };

const SqlQualityModal = ({ open, onClose, connections, defaultConnectionId, aiEnabled, aiProvider, onOpenGuides, onOpenInEditor, onOpenBuilder, restore, onView, focus }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const conns = (connections || []).filter(c => SUPPORTED.includes(c.dbType || 'oracle'));
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState('');
  const [job, setJob] = useState(null);
  const [step, setStep] = useState('target');
  const [mode, setMode] = useState(null);             // null | 'new'
  const [side, setSide] = useState('jobs');           // 왼쪽 목록 — jobs = 검사 이력, criteria = 검사 기준
  const [critSel, setCritSel] = useState(null);       // 고른 기준 { id } · 새 기준 { id: null, copyOf? }
  const [switchTo, setSwitchTo] = useState('');       // ① 기준 단계에서 바꿀 기준
  const editCriteria = (id) => { setSide('criteria'); setCritSel(id ? { id } : null); };
  const [newForm, setNewForm] = useState(null);
  const [criteriaList, setCriteriaList] = useState([]);
  const [target, setTarget] = useState(emptyTarget);
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState('');
  const [critHist, setCritHist] = useState(null);
  const fileRef = useRef(null);
  const [collectSets, setCollectSets] = useState([]);   // SQL 수집 세트 — 대상 「수집 세트」 에서 고른다
  const tRef = useRef(t);
  tRef.current = t;

  const reload = useCallback(async () => {
    try { setJobs((await api.listJobs(q)).jobs || []); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [q]);
  const reloadCriteria = useCallback(async () => {
    try { setCriteriaList((await api.listCriteria()).criteria || []); } catch (_e) { setCriteriaList([]); }
  }, []);
  useEffect(() => { if (open) { reload(); reloadCriteria(); } }, [open, reload, reloadCriteria]);

  // 새로고침 뒤 — 보던 작업·단계로 한 번만
  const [viewReady, setViewReady] = useState(false);
  useEffect(() => {
    if (!open || viewReady) return;
    if (!(restore && restore.id)) { setViewReady(true); return; }
    let alive = true;
    api.getJob(restore.id).then((r) => { if (!alive) return; setJob(r.job); if (restore.step && TABS.includes(restore.step)) setStep(restore.step); })
      .catch(() => {}).finally(() => { if (alive) setViewReady(true); });
    return () => { alive = false; };   // eslint-disable-line consistent-return
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 열릴 때 한 번
  }, [open]);
  const viewJobId = job ? job.id : null;
  useEffect(() => { if (open && viewReady && onView) onView({ id: viewJobId, step }); }, [open, viewReady, viewJobId, step, onView]);

  const openJob = async (id, wantStep) => {
    try { const r = await api.getJob(id); setJob(r.job); setMode(null); setReport(''); setCritHist(null); setStep(wantStep && TABS.includes(wantStep) ? wantStep : (r.job.summary ? 'results' : 'target')); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  // 채팅 에이전트가 가리킨 검사로(sql_quality_open) — 화면이 열린 뒤 한 번. 항목을 가리켰으면 결과 단계로
  const focusNonce = focus && focus.nonce;
  useEffect(() => {
    if (!open || !focus || !focus.id) return;
    openJob(focus.id, focus.step || (focus.itemId ? 'results' : undefined));
  }, [open, focusNonce]);   // eslint-disable-line react-hooks/exhaustive-deps -- nonce 가 바뀔 때만
  const startNew = () => {
    setMode('new');
    setNewForm({ title: '', criteriaId: (criteriaList[0] && criteriaList[0].id) || '', connectionId: (defaultConnectionId && conns.some(c => c.id === defaultConnectionId)) ? defaultConnectionId : '' });
  };
  const create = async () => {
    setBusy('create');
    try {
      const r = await api.createJob({ title: newForm.title, criteriaId: newForm.criteriaId, connectionId: newForm.connectionId || null });
      setJob(r.job); setMode(null); setStep('target'); setTarget({ ...emptyTarget(), source: r.job.connectionId ? 'pool' : 'csv' }); reload();
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };
  // 샘플 검사 — 검사기마다 걸려야 할 쿼리·걸리면 안 될 쿼리를 검증용 기준으로 돌려 기대 결과와 대조한다(서버 samples.js).
  // 처음 목록을 열 때 하나 만들어지고, 지웠거나 결과를 새로 보고 싶으면 여기서 다시 만든다(있던 샘플 검사는 바뀐다)
  const makeSample = async () => {
    const had = jobs.some(j => j.target && j.target.source === 'sample');
    if (had) {
      const ok = await confirm({ title: t('common:sqq.sampleRemakeTitle'), message: t('common:sqq.sampleRemakeBody'), confirmText: t('common:sqq.sampleRemake') });
      if (!ok) return;
    }
    setBusy('sample');
    try { const r = await api.createSampleJob(); await reload(); if (r.job) openJob(r.job.id, 'results'); } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };
  const removeJob = async () => {
    const ok = await confirm({ title: t('common:sqq.jobDeleteTitle'), message: t('common:sqq.jobDeleteBody', { title: job.title }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    try { await api.deleteJob(job.id); setJob(null); reload(); } catch (err) { toast.error(formatApiError(err, t)); }
  };

  useEffect(() => {
    if (!open || !job || step !== 'target') return;
    collectApi.listSets().then(r => setCollectSets(r.sets || [])).catch(() => setCollectSets([]));
  }, [open, step, job]);

  const readCsv = (file) => {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => setTarget(tg => ({ ...tg, csvText: String(fr.result || ''), csvName: file.name }));
    fr.readAsText(file);
  };
  const loadTarget = async () => {
    setBusy('target');
    try {
      const { csvName, awrOk, ...spec } = target;   // eslint-disable-line no-unused-vars
      const r = await api.loadTarget(job.id, spec);
      setJob(r.job); reload();
      toast.success(t('common:sqq.targetLoaded', { count: r.job.target.count }));
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };

  const loadReport = useCallback(async () => {
    if (!job) return;
    try { setReport((await api.getReport(job.id)).markdown || ''); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [job]);
  useEffect(() => { if (open && job && step === 'status') { setReport(''); loadReport(); } },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 단계·작업이 바뀔 때
    [open, step, job && job.id, job && job.updatedAt]);
  useEffect(() => {
    if (!open || !job || step !== 'history' || !job.criteriaId) return;
    api.getCriteria(job.criteriaId).then(r => setCritHist(r.criteria)).catch(() => setCritHist(null));
  }, [open, step, job]);
  const download = async () => {
    const g = await guardExport(report, { scan: guardrailApi.scanText, redact: guardrailApi.redactText });
    if (!g) return;
    if (g.mode === 'unchecked') toast.warning(t('common:grd.exportUnchecked'));
    const blob = new Blob([g.text], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sql-quality-${(job.title || job.id).replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 60)}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  // 개발자 전달용 CSV — 고치는 사람이 자기 쿼리의 어느 줄이 왜 걸렸는지 본다(모든 검사 문장 한 파일, 원문 각 줄 끝에 `-- [위반]`).
  // SQL 원문이 실리므로 md 리포트와 같은 가드레일 검사를 지난다
  const [csvBusy, setCsvBusy] = useState(false);
  const downloadDevCsv = async () => {
    setCsvBusy(true);
    try {
      const { csv } = await api.getDeveloperCsv(job.id);
      const g = await guardExport(csv, { scan: guardrailApi.scanText, redact: guardrailApi.redactText });
      if (!g) return;
      if (g.mode === 'unchecked') toast.warning(t('common:grd.exportUnchecked'));
      const blob = new Blob([g.text], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `sql-quality-${(job.title || job.id).replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 60)}-dev.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setCsvBusy(false); }
  };
  // 지금 판으로 다시 가져오기(criteriaId 없음) 또는 다른 기준으로 바꾸기 — 결과를 맞추려면 검사를 다시 돌려야 한다
  const refreshCriteria = async (criteriaId) => {
    const ok = await confirm({ title: t('common:sqq.refreshTitle'), message: t(criteriaId ? 'common:sqq.switchBody' : 'common:sqq.refreshBody'), confirmText: t(criteriaId ? 'common:sqq.switchCriteria' : 'common:sqq.refresh') });
    if (!ok) return;
    try { const r = await api.refreshCriteria(job.id, criteriaId); setJob(r.job); setSwitchTo(''); toast.info(t('common:sqq.refreshed')); } catch (err) { toast.error(formatApiError(err, t)); }
  };

  // ── 단계 ─────────────────────────────────────────────
  const renderCriteria = () => {
    const c = job.criteria || {};
    const on = Object.entries(c.checks || {}).filter(([, v]) => v.on);
    return (
      <div className="stn-step">
        <div className="stn-row stn-between">
          <span><b>{c.name}</b> <span className="stn-note">{t('common:sqq.critAt', { at: storedTime(c.at, 'min') })}</span></span>
          <span className="stn-row">
            <button type="button" className="app-btn" onClick={() => refreshCriteria()}><FiRefreshCw size={12} /> {t('common:sqq.refresh')}</button>
            <button type="button" className="app-btn" onClick={() => editCriteria(job.criteriaId)}><FiSliders size={12} /> {t('common:sqq.editThisCriteria')}</button>
          </span>
        </div>
        <span className="stn-note">{t('common:sqq.snapshotHint')}</span>
        <div className="stn-row">
          <select value={switchTo} onChange={e => setSwitchTo(e.target.value)} aria-label={t('common:sqq.switchCriteria')}>
            <option value="">{t('common:sqq.pickOtherCriteria')}</option>
            {criteriaList.filter(x => x.id !== job.criteriaId).map(x => <option key={x.id} value={x.id}>{x.name}{x.builtin ? ` (${t('common:sqq.builtinBadge')})` : ''}</option>)}
          </select>
          <button type="button" className="app-btn" disabled={!switchTo} onClick={() => refreshCriteria(switchTo)}>{t('common:sqq.switchCriteria')}</button>
        </div>
        <CriteriaSummary body={c} t={t} />
        <div className="stn-box">
          <b>{t('common:sqq.checksOn', { n: on.length })}</b>
          <ul className="sqq-hist">{on.map(([id, v]) => <li key={id}><span className={`sqq-sev s-${v.severity}`}>{t(`common:sqq.sev.${v.severity}`)}</span> {t(`common:sqq.check.${id}`)} <span className="stn-note">{t(`common:sqq.checkDesc.${id}`)}</span></li>)}</ul>
        </div>
        <div className="stn-box">
          <b>{t('common:sqq.rulesTitle')} ({(c.guideRules || []).length})</b>
          {!(c.guideRules || []).length && <span className="stn-note">{t('common:sqq.noRulesPicked')}</span>}
          <ul className="sqq-hist">{(c.guideRules || []).map((r, i) => <li key={r.ruleId}><b>R{i + 1}</b> {r.principle} <span className="stn-note">— {r.guideTitle}</span></li>)}</ul>
        </div>
        <div className="stn-box">
          <b>{t('common:sqq.group.pattern')} ({(c.patterns || []).length})</b>
          <ul className="sqq-hist">{(c.patterns || []).map(p => (
            <li key={p.id}><b>{p.name}</b> <span className="stn-note">— {t(`common:sqq.patternModes.${p.mode}`)}{p.mode === 'sample' ? ` · ${t('common:sqq.patternThreshold')} ${p.threshold}` : ` · ${(p.include || []).join(' + ')}`}{p.on === false ? ` · ${t('common:sqq.patternOff')}` : ''}</span></li>
          ))}</ul>
        </div>
        <div className="stn-box">
          <b>{t('common:sqq.bizTitle')} ({(c.biz || []).length})</b>
          <ul className="sqq-hist">{(c.biz || []).map((b, i) => (
            // eslint-disable-next-line react/no-array-index-key -- 순서가 곧 우선순위다
            <li key={i}>{t(`common:sqq.bizField.${b.field}`)} {t(`common:sqq.bizOp.${b.op}`)} <span className="mono">{b.value}</span> → <b>{b.label}</b></li>
          ))}</ul>
        </div>
      </div>
    );
  };

  const renderTarget = () => {
    const tg = target;
    const set = (k) => (e) => setTarget({ ...tg, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const cur = job.target;
    return (
      <div className="stn-step">
        {cur && <div className="stn-box sqq-fade"><b>{t('common:sqq.currentTarget')}</b>
          <span>{t('common:sqq.targetLine', { src: cur.source === 'set' ? `${t('common:sqq.src.set')} · ${cur.setTitle || ''}${cur.text === 'red' ? ` (${t('common:sqq.setTextOpt.red')})` : ''}` : t(`common:sqq.src.${cur.source}`), count: fmt(cur.count), at: storedTime(cur.at, 'min') })}</span>
          {(cur.notes || []).map(n => <span key={n} className="sau-notice">{t(`common:sqq.targetNote.${n.split(':')[0]}`, { defaultValue: n, n: n.split(':')[1] })}</span>)}
        </div>}
        <div className="stn-row sau-mode" role="radiogroup" aria-label={t('common:sqq.source')}>
          {['pool', 'awr', 'csv', 'set'].map((s) => {
            const isPg = job.dbType === 'postgresql';
            const off = (!['csv', 'set'].includes(s) && !job.connectionId) || (s === 'awr' && isPg);
            return (
              <label key={s} className={`sau-mode-opt${tg.source === s ? ' on' : ''}${off ? ' sqq-disabled' : ''}`}>
                <input type="radio" name="sqq-src" checked={tg.source === s} disabled={off} onChange={() => setTarget({ ...tg, source: s })} />
                <b>{t(`common:sqq.src.${s}`)}</b><span className="stn-note">{t(isPg && ['pool', 'awr'].includes(s) ? `common:sqq.srcHintPg.${s}` : `common:sqq.srcHint.${s}`)}</span>
              </label>
            );
          })}
        </div>
        {!job.connectionId && <span className="stn-note">{t('common:sqq.noConnHint')}</span>}
        {tg.source === 'set' && (
          <div className="stn-box sqq-fade" key="set">
            <label className="stn-field"><span>{t('common:sqq.pickSet')}</span>
              <select value={tg.setId} onChange={e => setTarget({ ...tg, setId: e.target.value })}>
                <option value="">{t(collectSets.length ? 'common:sqq.pickSetPh' : 'common:sqq.noSets')}</option>
                {collectSets.map(x => <option key={x.id} value={x.id}>{x.title} ({fmt(x.summary ? x.summary.kept : x.items)})</option>)}
              </select>
            </label>
            <div className="stn-row" role="radiogroup" aria-label={t('common:sqq.setText')}>
              {['clean', 'red'].map(x => {
                const picked = collectSets.find(y => y.id === tg.setId);
                const noRed = x === 'red' && !(picked && picked.redact && !picked.redact.off && picked.redact.counts);
                return <label key={x} className={`stn-row${noRed ? ' sqq-disabled' : ''}`}><input type="radio" name="sqq-set-text" checked={tg.text === x} disabled={noRed} onChange={() => setTarget({ ...tg, text: x })} /> {t(`common:sqq.setTextOpt.${x}`)}</label>;
              })}
            </div>
            <span className="stn-note">{t('common:sqq.setHint')}</span>
          </div>
        )}
        {['pool', 'awr'].includes(tg.source) && (
          <div className="stn-box sqq-fade" key="db">
            <div className="stn-row">
              <span className="stn-note">{t('common:sqq.kinds')}</span>
              {KINDS.map(k => <label key={k} className="stn-row"><input type="checkbox" checked={tg.kinds.includes(k)} onChange={e => setTarget({ ...tg, kinds: e.target.checked ? [...tg.kinds, k] : tg.kinds.filter(x => x !== k) })} /> {k.toUpperCase()}</label>)}
            </div>
            <div className="stn-grid2">
              <label className="stn-field"><span>{t('common:sqq.schema')}</span><input value={tg.schema} onChange={set('schema')} placeholder={t('common:sqq.schemaPh')} /></label>
              <label className="stn-field"><span>{t('common:sqq.module')}</span><input value={tg.module} onChange={set('module')} placeholder={t('common:sqq.modulePh')} /></label>
              <label className="stn-field"><span>{t('common:sqq.textFilter')}</span><input value={tg.q} onChange={set('q')} placeholder={t('common:sqq.textFilterPh')} /></label>
              <span className="stn-row">
                <label className="stn-field"><span>{t('common:sqq.limit')}</span><input type="number" min="1" max="5000" className="sqq-num" value={tg.limit} onChange={set('limit')} /></label>
                <label className="stn-field"><span>{t('common:sqq.minExec')}</span><input type="number" min="0" className="sqq-num" value={tg.minExec} onChange={set('minExec')} /></label>
                {tg.source === 'awr' && <label className="stn-field"><span>{t('common:sqq.days')}</span><input type="number" min="1" max="31" className="sqq-num" value={tg.days} onChange={set('days')} /></label>}
              </span>
            </div>
            {tg.source === 'awr' && <label className="stn-row sau-notice"><input type="checkbox" checked={tg.awrOk} onChange={set('awrOk')} /> {t('common:sqq.awrLicense')}</label>}
            <span className="stn-note">{t('common:sqq.dbMaskHint')}</span>
          </div>
        )}
        {tg.source === 'csv' && (
          <div className="stn-box sqq-fade" key="csv">
            <div className="stn-row">
              <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" style={{ display: 'none' }} onChange={e => readCsv(e.target.files && e.target.files[0])} />
              <button type="button" className="app-btn" onClick={() => fileRef.current && fileRef.current.click()}><FiUpload size={12} /> {t('common:sqq.pickCsv')}</button>
              <span className="stn-note">{tg.csvName ? t('common:sqq.csvPicked', { name: tg.csvName, kb: Math.ceil(tg.csvText.length / 1024) }) : t('common:sqq.csvNone')}</span>
              <select value={tg.delimiter || 'comma'} onChange={e => setTarget({ ...tg, delimiter: e.target.value })} aria-label={t('common:sqq.delimiter')}>
                {['comma', 'tab', 'semicolon', 'pipe'].map(d => <option key={d} value={d}>{t(`common:sqq.delim.${d}`)}</option>)}
              </select>
            </div>
            <span className="stn-note">{t('common:sqq.csvHint')}</span>
          </div>
        )}
        {busy === 'target' && <div className="stn-progress">{t('common:sqq.loadingTarget')}</div>}
        <div className="stn-actions">
          {cur && <label className="stn-row"><input type="checkbox" checked={!tg.replace} onChange={e => setTarget({ ...tg, replace: !e.target.checked })} /> {t('common:sqq.append')}</label>}
          {busy === 'target'
            ? <button type="button" className="app-btn app-btn-danger" onClick={() => api.cancelJob(job.id).catch(() => {})}><FiSquare size={12} /> {t('common:sqq.stop')}</button>
            : <button type="button" className="app-btn app-btn-primary" onClick={loadTarget}
              disabled={!!busy || (tg.source === 'set' ? !tg.setId : (tg.source === 'csv' ? !tg.csvText : (!tg.kinds.length || (tg.source === 'awr' && !tg.awrOk))))}>{t('common:sqq.loadTarget')}</button>}
          {cur && <button type="button" className="app-btn" onClick={() => setStep('results')}>{t('common:sqq.next')}</button>}
        </div>
      </div>
    );
  };

  const renderStatus = () => {
    const s = job.summary;
    if (!s) return <div className="stn-step"><div className="stn-note">{t('common:sqq.noSummary')}</div></div>;
    return (
      <div className="stn-step">
        <div className="sqq-kpis">
          {[['items', s.items], ['flagged', s.flagged], ['analyzed', s.analyzed || 0], ['reviewed', s.reviewed || 0]].map(([k, v]) => (
            <div key={k} className="sqq-kpi"><span className="stn-note">{t(`common:sqq.kpi.${k}`)}</span><b>{fmt(v)}</b></div>
          ))}
        </div>
        <div className="stn-grid2">
          <Bars title={t('common:sqq.byCheck')} data={s.byCheck} label={k => t(`common:sqq.check.${k}`)} />
          <Bars title={t('common:sqq.byBiz')} data={s.byBiz} label={k => k || t('common:sqq.noBiz')} />
          <Bars title={t('common:sqq.bySeverity')} data={s.bySev} label={k => t(`common:sqq.sev.${k}`)} />
          <Bars title={t('common:sqq.byVerdict')} data={s.byVerdict} label={k => t(`common:sqq.verdict.${k}`)} />
        </div>
        <ReportView
          markdown={report}
          loadingText={t('common:sqq.loading')}
          toolbar={(
            <div className="stn-row stn-between">
              <span className="stn-note">{t('common:sqq.reportHint')}</span>
              <span className="stn-row">
                <button type="button" className="app-btn" onClick={loadReport}><FiRefreshCw size={12} /> {t('common:sqq.refreshReport')}</button>
                <button type="button" className="app-btn" onClick={downloadDevCsv} disabled={csvBusy} title={t('common:sqq.devCsvHint')}><FiDownload size={12} /> {t('common:sqq.devCsv')}</button>
                <button type="button" className="app-btn app-btn-primary" onClick={download} disabled={!report}><FiDownload size={12} /> {t('common:sqq.download')}</button>
              </span>
            </div>
          )}
        />
      </div>
    );
  };

  const renderHistory = () => (
    <div className="stn-step">
      <div className="stn-box">
        <b>{t('common:sqq.jobHistory')}</b>
        <div className="stn-table-wrap">
          <table className="stn-table">
            <thead><tr><th>{t('common:sqq.histAt')}</th><th>{t('common:sqq.histWho')}</th><th>{t('common:sqq.histWhat')}</th><th>{t('common:sqq.histNote')}</th></tr></thead>
            <tbody>
              {[...(job.history || [])].reverse().map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key -- 이력은 순서가 곧 키다
                <tr key={i}><td className="mono">{storedTime(h.at, 'min')}</td><td>{t(`common:sqq.actor.${h.actor}`, { defaultValue: h.actor })}</td>
                  <td>{t(`common:sqq.action.${h.action}`, { defaultValue: h.action })}</td><td className="stn-small">{h.note}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="stn-box">
        <div className="stn-row stn-between"><b>{t('common:sqq.critHistoryOf', { name: (job.criteria && job.criteria.name) || '' })}</b>
          {onOpenGuides && <button type="button" className="app-btn" onClick={onOpenGuides}>{t('common:sqq.ruleHistoryLink')}</button>}</div>
        {!critHist && <span className="stn-note">{t('common:sqq.critGone')}</span>}
        {critHist && (
          <ul className="sqq-hist">
            {[...(critHist.history || [])].reverse().map((h, i) => (
              // eslint-disable-next-line react/no-array-index-key -- 이력은 순서가 곧 키다
              <li key={i}><span className="mono">{storedTime(h.at, 'min')}</span> {(h.changes || []).map(c => describeChange(c, t)).join(' · ') || t('common:sqq.hist.noChange')}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const body = !job ? null : {
    criteria: renderCriteria, target: renderTarget,
    results: () => <QualityResults job={job} onJobChanged={(j) => { setJob(j); reload(); }} aiEnabled={aiEnabled} aiProvider={aiProvider} connections={conns} onOpenInEditor={onOpenInEditor} onOpenBuilder={onOpenBuilder} />,
    status: renderStatus, history: renderHistory,
  }[step]();

  return (
    <AppModal windowed open={open} onClose={onClose} title={t('common:sqq.windowTitle')} icon={<FiCheckSquare size={14} />} className="app-modal-default stn-modal">
      <div className="stn-body">
        <aside className="stn-side">
          {/* 왼쪽 목록은 하나씩 — 검사(이력)와 검사 기준. 고른 쪽의 작업을 오른쪽이 단계(리본)로 보인다 */}
          <div className="stn-row stn-report-mode sqq-side-switch" role="tablist" aria-label={t('common:sqq.sideSwitch')}>
            {['jobs', 'criteria'].map(s => (
              <button key={s} type="button" role="tab" aria-selected={side === s} className={`app-btn${side === s ? ' on' : ''}`} onClick={() => setSide(s)}>
                {s === 'jobs' ? <FiCheckSquare size={12} /> : <FiSliders size={12} />} {t(`common:sqq.side.${s}`)}
              </button>
            ))}
          </div>
          {side === 'jobs' ? (
            <>
              <button type="button" className="app-btn app-btn-primary stn-full" onClick={startNew}><FiPlus size={12} /> {t('common:sqq.newJob')}</button>
              <button type="button" className="app-btn stn-full" onClick={makeSample} disabled={busy === 'sample'} title={t('common:sqq.sampleJobHint')}><FiCheckSquare size={12} /> {t('common:sqq.sampleJob')}</button>
              <input className="stn-full" value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:sqq.jobSearch')} onKeyDown={e => { if (e.key === 'Enter') reload(); }} />
              <ul className="stn-jobs sqq-fade" key="jobs">
                {jobs.map(j => (
                  <li key={j.id} className={job && job.id === j.id && !mode ? 'on' : ''} onClick={() => openJob(j.id)}>
                    <div className="stn-job-title">{j.title}</div>
                    <div className="stn-job-meta">{t(`common:sqq.status.${j.status}`, { defaultValue: j.status })} · {fmt(j.items)} · {storedTime(j.updatedAt, 'date')}</div>
                  </li>
                ))}
                {!jobs.length && <li className="stn-empty">{t('common:sqq.noJobs')}</li>}
              </ul>
            </>
          ) : (
            <>
              <button type="button" className="app-btn app-btn-primary stn-full" onClick={() => setCritSel({ id: null, nonce: Date.now() })}><FiPlus size={12} /> {t('common:sqq.newCriteria')}</button>
              <ul className="stn-jobs sqq-fade" key="criteria">
                {criteriaList.map(c => (
                  <li key={c.id} className={critSel && critSel.id === c.id ? 'on' : ''} onClick={() => setCritSel({ id: c.id })}>
                    <div className="stn-job-title">{c.name}{c.builtin && <span className="sqq-tag sqq-tag-builtin">{t('common:sqq.builtinBadge')}</span>}</div>
                    <div className="stn-job-meta">{c.builtin ? t('common:sqq.builtinMeta') : storedTime(c.updatedAt, 'min')}</div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>
        <section className="stn-main">
          {side === 'criteria' && (
            <CriteriaEditor
              selected={critSel}
              onOpenGuides={onOpenGuides}
              onSaved={(c) => { reloadCriteria(); setCritSel({ id: c.id }); }}
              onDeleted={() => { reloadCriteria(); setCritSel(null); }}
              onCopy={(src) => setCritSel({ id: null, copyOf: src, nonce: Date.now() })}
            />
          )}
          {side === 'jobs' && <>
          {mode === 'new' && newForm && (
            <div className="stn-new sqq-fade">
              <label className="stn-field"><span>{t('common:sqq.title')}</span>
                <input value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} placeholder={t('common:sqq.titlePh')} /></label>
              <div className="stn-grid2">
                <label className="stn-field"><span>{t('common:sqq.criteria')} <em className="sau-req">*</em></span>
                  <select value={newForm.criteriaId} onChange={e => setNewForm({ ...newForm, criteriaId: e.target.value })}>
                    {!criteriaList.length && <option value="">{t('common:sqq.noCriteria')}</option>}
                    {criteriaList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label className="stn-field"><span>{t('common:sqq.connection')}</span>
                  <select value={newForm.connectionId} onChange={e => setNewForm({ ...newForm, connectionId: e.target.value })}>
                    <option value="">{t('common:sqq.noConnection')}</option>
                    {conns.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                  </select>
                </label>
              </div>
              {!criteriaList.length && <div className="stn-row"><span className="stn-note">{t('common:sqq.needCriteria')}</span><button type="button" className="app-btn" onClick={() => editCriteria(null)}><FiSliders size={12} /> {t('common:sqq.manageCriteria')}</button></div>}
              <span className="stn-note">{t('common:sqq.newHint')}</span>
              <div className="stn-actions">
                <button type="button" className="app-btn" onClick={() => setMode(null)}>{t('common:ui.cancel')}</button>
                <button type="button" className="app-btn app-btn-primary" onClick={create} disabled={!!busy || !newForm.criteriaId}><FiPlus size={12} /> {t('common:sqq.create')}</button>
              </div>
            </div>
          )}
          {!mode && !job && <div className="stn-empty-main">{t('common:sqq.intro')}</div>}
          {!mode && job && (
            <>
              {/* 검사 작업은 단계에서 고쳐 저장할 문서가 없다(대상·결과는 그 단계의 단추가 바로 반영) — 저장 단추 없이 삭제만 */}
              <WorkflowHead title={job.title}
                meta={`${job.connName || t(job.target && job.target.source === 'sample' ? 'common:sqq.sampleNoConn' : 'common:sqq.noConnection')} · ${(job.criteria && job.criteria.name) || ''} · ${t(`common:sqq.status.${job.status}`, { defaultValue: job.status })}`}
                actions={job.ai && job.ai.calls > 0 ? <span className="stn-note">{t('common:sqq.aiUsage', { calls: job.ai.calls, tokens: fmt(job.ai.tokens), cost: job.ai.costUsd })}</span> : null}
                onDelete={removeJob} />
              <WorkflowRibbon tabs={TABS} step={step} onStep={setStep} label={s => t(`common:sqq.steps.${s}`)}
                suffix={(s) => {
                  if (s === 'target' && job.target) return ` (${fmt(job.target.count)})`;
                  // 걸린 것 / 전체 — 걸린 수만 적으면 대상(21)과 달라 나머지가 빠진 것처럼 보였다(2026-10-31)
                  if (s === 'results' && job.summary) return ` (${fmt(job.summary.flagged)}/${fmt(job.summary.items)})`;
                  return '';
                }} />
              {step !== 'history' && <div className="srv-stepHelp" key={`help-${step}`}>{t(`common:sqq.stepHelp.${step}`)}</div>}
              <div className="stn-step-wrap" key={`${job.id}-${step}`}>{body}</div>
            </>
          )}
          </>}
        </section>
      </div>
    </AppModal>
  );
};

SqlQualityModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  connections: PropTypes.arrayOf(PropTypes.object),
  defaultConnectionId: PropTypes.string,
  aiEnabled: PropTypes.bool,
  aiProvider: PropTypes.string,
  /** 가이드 관리 창(SQL 표준) — AI 빌드에만 */
  onOpenGuides: PropTypes.func,
  /** 편집기 새 탭으로 (sql) */
  onOpenInEditor: PropTypes.func,
  /** SQL 빌더로 (sql) */
  onOpenBuilder: PropTypes.func,
  restore: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string }),
  /** 채팅 에이전트가 가리킨 작업(SSE open_screen, 2026-10-20) — nonce 가 바뀔 때마다 그 작업·단계를 연다 */
  focus: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string, nonce: PropTypes.number }),
  onView: PropTypes.func,
};

// 편집기에 글자를 칠 때마다 App 이 다시 그려져도 값이 같으면 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 입력 지연)
export default memoWindow(SqlQualityModal);
